import React, { useRef } from 'react'
import {
  ContainerDashboardMatrix,
  ContainerEllipseAccordion,
  ContainerRow,
  ContainerRowUncontrolled,
  FixedContainerHealthProcedure,
  ItemContainer,
  VerticalLine
} from './styles'
import {
  daysPeriodWasSelected,
  EHealthProceduresTypes,
  IHealthProcedures,
  IHealthProceduresOmics,
  monthsPeriodWasSelected,
  yearsPeriodWasSelected,
  IAdmissionRowSlots,
  IDiagnosisRowSlots
} from '~/interfaces'
import { useOncologyHealthProcedures, useOpenCloseUpdater } from '~/context'
import {
  hasObjectValue,
  timelineFilters,
  filterHealthProceduresPer,
  isOmics,
  isConduct,
  byTypeExceptDiagnosis,
  byTypeEqualToDiagnosis
} from '~/utils'
import {
  TimelineOmics,
  TimelineEllipseButton,
  RowContentPart,
  RowInfoPart,
  TimelineYearsAccordion,
  TimelineRectangleButton,
  AlertMessage,
  BiRadsBadge,
  TimelineConducts
} from '~/components/shared'
import { useDraggableScroll } from '~/hooks/useDraggableScroll'
import {
  mapperTimelineViewByDaysFrom,
  mapperTimelineViewByMonthsFrom,
  mapperTimelineViewByYearsFrom
} from '~/utils/dashboard/timeline/oncology'
import { isHealthProceduresGroupsEquals } from '~/utils/dashboard/timeline/mappers'
import { message } from '~/services'

export const TimelineOncology: React.FC = (): JSX.Element => {
  const { open: openSidePanel } = useOpenCloseUpdater()
  const {
    displayedHealthProcedures,
    setOpenConductCardList,
    setOpenOmicsCardList,
    typeFiltersActive,
    dateFiltersActive,
    selectedPeriod,
    selectMedicalEventForDetailing,

    filterTags,

    sidePanelEvents,
    updateSidePanelEvents,
    resetSidePanelEvents
  } = useOncologyHealthProcedures()

  const applyTimelineFilters = (healthProcedures: IHealthProcedures[]): IHealthProcedures[] => {
    const searchTerms = filterTags?.filter(tag => tag.type === 'searchTerm').map(tag => tag.text)
    return timelineFilters(healthProcedures)
      .perDate(dateFiltersActive)
      .perType(typeFiltersActive)
      .perMultipleTextTerms(searchTerms).value
  }

  const timelineRef = useRef(null)
  const { onMouseDown } = useDraggableScroll(timelineRef, { direction: 'vertical' })

  const handleSelectedHealthProcedure = (healthProcedure: IHealthProcedures[]): void => {
    const hasOnlyOneSelectedHealthProcedure = healthProcedure.length === 1
    const isMedicalEventSelectedEqualToTheListedInSidePanel = isHealthProceduresGroupsEquals(
      healthProcedure,
      sidePanelEvents
    )
    const isMedicalEventSelectedEqualToDisplayedEventsInEntireTimeline = isHealthProceduresGroupsEquals(
      healthProcedure,
      displayedHealthProcedures
    )
    const omicsInSelectedHealthProcedure = filterHealthProceduresPer(healthProcedure, isOmics)
    const conductsInSelectedHealthProcedure = filterHealthProceduresPer(healthProcedure, isConduct)

    if (hasOnlyOneSelectedHealthProcedure) {
      selectMedicalEventForDetailing(healthProcedure[0])
      return
    }

    if (
      !isMedicalEventSelectedEqualToDisplayedEventsInEntireTimeline &&
      isMedicalEventSelectedEqualToTheListedInSidePanel
    ) {
      resetSidePanelEvents()
      return
    }

    if (omicsInSelectedHealthProcedure.length > 0) {
      setOpenOmicsCardList(true)
    }
    if (conductsInSelectedHealthProcedure.length > 0) {
      setOpenConductCardList(true)
    }

    updateSidePanelEvents(healthProcedure)
    openSidePanel()
  }

  const renderBiRadsBadge = (healthProcedures: IHealthProcedures[]): JSX.Element | null => {
    let biRads: null | number = null

    healthProcedures.forEach(healthProcedure => {
      if (healthProcedure.biradsCategory && (!biRads || biRads < healthProcedure.biradsCategory)) {
        biRads = healthProcedure.biradsCategory
      }
    })

    if (biRads) {
      return <BiRadsBadge category={biRads} />
    }

    return null
  }

  const getTimelineViewByYears = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData }))
      return <AlertMessage>{message('healthProcedures.empty_list')}</AlertMessage>

    const structuredData = mapperTimelineViewByYearsFrom(filteredData)

    return (
      <>
        {structuredData.years.map((year, yearIndex) => {
          return (
            <ContainerEllipseAccordion key={year.id}>
              <TimelineYearsAccordion
                title={`${year.id} (${year.yearRowSlotsTotal})`}
                active={Number(yearIndex) === 0}
                content={
                  <ContainerRowUncontrolled>
                    {year.rowSlots.map(healthProceduresGrouped => {
                      const { healthProcedures } = healthProceduresGrouped

                      if (healthProceduresGrouped.type === EHealthProceduresTypes.DIAGNOSIS) {
                        const group = healthProcedures as IAdmissionRowSlots[]
                        return group.map(diagnosis => (
                          <FixedContainerHealthProcedure key={diagnosis.id}>
                            <TimelineRectangleButton
                              onClick={handleSelectedHealthProcedure}
                              healthProcedures={[diagnosis]}
                              position={0}
                              rowSize={100}
                              variant={'diagnosis'}
                            />
                          </FixedContainerHealthProcedure>
                        ))
                      }

                      return (
                        <ItemContainer key={healthProcedures[0].id}>
                          <FixedContainerHealthProcedure>
                            <TimelineEllipseButton
                              onClick={handleSelectedHealthProcedure}
                              healthProcedures={healthProcedures as IHealthProceduresOmics[]}
                              position={0}
                              quantity={healthProcedures.length === 1 ? 0 : healthProcedures.length}
                            />
                          </FixedContainerHealthProcedure>
                          {renderBiRadsBadge(healthProcedures)}
                        </ItemContainer>
                      )
                    })}
                  </ContainerRowUncontrolled>
                }
              />
            </ContainerEllipseAccordion>
          )
        })}
      </>
    )
  }

  const getTimelineViewByMonths = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData }))
      return <AlertMessage>{message('healthProcedures.empty_list')}</AlertMessage>

    const structuredData = mapperTimelineViewByMonthsFrom(filteredData)

    return (
      <>
        {structuredData.years.map((year, yearIndex, yearArray) => {
          return (
            <React.Fragment key={`year-${year.id}`}>
              <ContainerRow>
                <RowInfoPart isFilled={true} period={`${year.id} (${year.yearsRowSlotsTotal})`}>
                  <VerticalLine isFirst={yearIndex === 0} isLast={false} />
                </RowInfoPart>
                <RowContentPart isFull={true} isFilled={false}>
                  {/* HEADER ROW IS EMPTY */}
                </RowContentPart>
              </ContainerRow>

              {year.months.map((month, monthIndex, monthArray) => {
                const healthProceduresWithoutDiagnosis = filterHealthProceduresPer(
                  month.rowSlots,
                  byTypeExceptDiagnosis
                )
                const diagnosis = filterHealthProceduresPer(
                  month.rowSlots,
                  byTypeEqualToDiagnosis
                ) as IDiagnosisRowSlots[]

                return (
                  <ContainerRow key={`month-${month.id}`}>
                    <RowInfoPart isFilled={false} period={`${month.id.substring(0, 3)}`}>
                      <VerticalLine
                        isFirst={false}
                        isLast={monthIndex === monthArray.length - 1 && yearIndex === yearArray.length - 1}
                      />
                    </RowInfoPart>
                    <RowContentPart isFull={false} isFilled={false}>
                      {healthProceduresWithoutDiagnosis?.length > 0 && (
                        <TimelineOmics
                          period="months"
                          healthProcedures={healthProceduresWithoutDiagnosis}
                          handleClick={handleSelectedHealthProcedure}
                        />
                      )}

                      {diagnosis?.length > 0 && (
                        <TimelineConducts
                          variant="diagnosis"
                          period="months"
                          healthProcedures={diagnosis}
                          handleClick={handleSelectedHealthProcedure}
                        />
                      )}
                    </RowContentPart>
                  </ContainerRow>
                )
              })}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const getTimelineViewByDays = (healthProcedures: IHealthProcedures[]): JSX.Element => {
    const filteredData = applyTimelineFilters(healthProcedures)

    if (!hasObjectValue({ ...filteredData }))
      return <AlertMessage>{message('healthProcedures.empty_list')}</AlertMessage>

    const structuredData = mapperTimelineViewByDaysFrom(filteredData)
    return (
      <>
        {structuredData.months.map((month, monthIndex, monthArray) => {
          return (
            <React.Fragment key={`${month.id}-${new Date(month.date).getFullYear()}`}>
              <ContainerRow>
                <RowInfoPart
                  isFilled={true}
                  period={`${new Date(month.date).getFullYear()}\n${month.id.substring(0, 3)} (${
                    month.monthRowSlotsTotal
                  })`}
                >
                  <VerticalLine isFirst={monthIndex === 0} isLast={false} />
                </RowInfoPart>
                <RowContentPart isFull={true} isFilled={false}>
                  {/* HEADER ROW IS EMPTY */}
                </RowContentPart>
              </ContainerRow>

              {month.days.map((day, dayIndex, dayArray) => {
                const { rowSlots } = day
                const healthProceduresWithoutDiagnosis = filterHealthProceduresPer(rowSlots, byTypeExceptDiagnosis)
                const diagnosis = filterHealthProceduresPer(rowSlots, byTypeEqualToDiagnosis) as IDiagnosisRowSlots[]

                return (
                  <ContainerRow key={`${month.id}-${day.id}`}>
                    <RowInfoPart isFilled={false} period={`${day.id}`}>
                      <VerticalLine
                        isFirst={false}
                        isLast={monthIndex === monthArray.length - 1 && dayIndex === dayArray.length - 1}
                      />
                    </RowInfoPart>

                    <RowContentPart isFull={false} isFilled={false}>
                      {healthProceduresWithoutDiagnosis?.length > 0 && (
                        <TimelineOmics
                          period="days"
                          healthProcedures={day.rowSlots}
                          handleClick={handleSelectedHealthProcedure}
                        />
                      )}
                      {diagnosis?.length > 0 && (
                        <TimelineConducts
                          variant="diagnosis"
                          period="days"
                          healthProcedures={diagnosis}
                          handleClick={handleSelectedHealthProcedure}
                        />
                      )}
                    </RowContentPart>
                  </ContainerRow>
                )
              })}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const renderTimeline = () => {
    switch (selectedPeriod.period) {
      case 'years':
        return yearsPeriodWasSelected(selectedPeriod) ? getTimelineViewByYears(displayedHealthProcedures) : null
      case 'months':
        return monthsPeriodWasSelected(selectedPeriod) ? getTimelineViewByMonths(displayedHealthProcedures) : null
      case 'days':
        return daysPeriodWasSelected(selectedPeriod) ? getTimelineViewByDays(displayedHealthProcedures) : null
      default:
        return <AlertMessage>{message('healthProcedures.empty_list')}</AlertMessage>
    }
  }

  return (
    <>
      {displayedHealthProcedures ? (
        <ContainerDashboardMatrix ref={timelineRef} onMouseDown={onMouseDown}>
          {renderTimeline()}
        </ContainerDashboardMatrix>
      ) : null}
    </>
  )
}
